import { Component, inject, Input, OnInit } from '@angular/core';
import { getPpmMilestoneFormObject } from './utils/milestone-form-object';
import { RefParamsService } from '@/pages/settings/ref-params/ref-params.service';
import { Currency, RefTypeEnum } from '@/types';
import { BehaviorSubject, catchError, combineLatest, filter, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { FileService } from '@/components/shared/file-list/file-list.service';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { PpmMilestoneRepository, PpmMilestonesRepositoryFactory, PpmMileStoneStore } from '@/store/ppm-state/milestones-state/milestones.repository';
import { PassingModeService } from '@/pages/settings/passing-mode/passing-mode.service';
import { CurrencyService } from '@/pages/settings/currency/currency.service';
import { FundingSourceService } from '@/pages/settings/funding-sources/funding-sources.service';

@Component({
  selector: 'sm-ppm-milestone-form',
  imports: [FormBuilderComponent, CommonModule, SpinnerComponent],
  templateUrl: './milestone-form.component.html',
  styleUrl: './milestone-form.component.scss',
})
export class PpmMilestoneFormComponent implements OnInit {
  //services
  fundingSourceService = inject(FundingSourceService);
  refParamsService = inject(RefParamsService);
  passingModeService = inject(PassingModeService);
  currencyService = inject(CurrencyService);
  fileService = inject(FileService);

  //inputs
  @Input() store?: PpmMileStoneStore;
  @Input() isDisabled: Observable<boolean> = of(false);
  @Input() acId?: Observable<number>;
  @Input() minUpdateDate?: Date;
  @Input() actualizedCount?: Observable<number>;

  loading$ = new BehaviorSubject<boolean>(true);

  milestoneRepository?: PpmMilestoneRepository;

  marketTypesOptions: SelectOption[] = [];
  financialSourcesOptions: (SelectOption & { examenPrealable: boolean })[] = [];
  selectionModeOptions: SelectOption[] = [];
  currencyOptions: SelectOption[] = [];
  currencies: Currency[] = [];
  //form-object
  formObject: DynamicForm = getFormBuilderObjectFactory();

  ngOnInit() {
    this.loadFormOptions();
  }

  private loadFormOptions() {
    this.loading$.next(true);
    forkJoin({
      marketTypes: this.refParamsService.getSpecializedRefParams(RefTypeEnum.MARKET_TYPE),
      financialSources: this.fundingSourceService.getAll(),
      currencies: this.currencyService.getAll(),
    }).subscribe(({ marketTypes, financialSources, currencies }) => {
      this.marketTypesOptions = marketTypes.map(ref => ({ name: ref.description, code: ref.id }));
      this.financialSourcesOptions = financialSources.map(ref => ({ name: ref.description, code: ref.id, examenPrealable: ref.bailleurDeFonds }));
      this.currencyOptions = currencies.map(curr => ({ name: curr.description, code: curr.id }));
      this.currencies = currencies;

      this.buildFormObject();
      this.setupSelectionModeLoader();

      this.loading$.next(false);
    });
  }

  private setupSelectionModeLoader() {
    const typeMarcher$ = this.milestoneRepository?.getNestedObservable<number>('milestoneDto', 'typeMarcher.id', false);
    if (!typeMarcher$) return;

    this.loading$.next(true);

    typeMarcher$
      .pipe(
        filter((id): id is number => !!id),
        switchMap(typeMarcherId => this.passingModeService.getByMarketType(typeMarcherId).pipe(catchError(() => of([]))))
      )
      .subscribe(selectionModes => {
        this.selectionModeOptions = selectionModes.map(ref => ({
          name: ref.libelle,
          code: ref.id,
        }));
        this.buildFormObject();
        this.loading$.next(false);
      });
  }

  private buildFormObject() {
    if (this.store) {
      this.milestoneRepository = PpmMilestonesRepositoryFactory.create(this.store);
      const usedInDac = this.milestoneRepository.getNestedObservable<boolean>('milestone', 'usedInDac', false);
      this.formObject = getPpmMilestoneFormObject({
        store: this.milestoneRepository,
        marketTypesOptions: this.marketTypesOptions,
        financialSourcesOptions: this.financialSourcesOptions,
        currencyOptions: this.currencyOptions,
        selectionModeOptions: this.selectionModeOptions,
        isDisabled: combineLatest([this.isDisabled, usedInDac]).pipe(map(([d1, d2]) => d1 || d2)),
        minDate: this.minUpdateDate,
        currencies: this.currencies,
        actualizedCount: this.actualizedCount,
      });
    }
  }
}
